import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from './config/react-query.config';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { RBACProvider } from './context/RBACContext';
import { ConnectionStatusProvider } from './context/ConnectionStatusContext'; // Import the new provider
import { NotificationProvider } from './context/NotificationContext';
import { ThemeSyncProvider } from './components/ThemeSyncProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import SidebarLayout from './components/layout/SidebarLayout';
import ConnectionStatusAlert from './components/ConnectionStatusAlert'; // Import the alert component
import SessionWarning from './components/SessionWarning'; // Import session warning component

// Create a single QueryClient instance for the entire app
const queryClient = createQueryClient();

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import DashboardSample from './pages/DashboardSample';
import OrganizationCreate from './pages/OrganizationCreate';
import OrganizationDetail from './pages/OrganizationDetail';
import TeamCreate from './pages/TeamCreate';
import TeamDetail from './pages/TeamDetail';
import Profile from './pages/Profile';
import PrivacyCenter from './pages/PrivacyCenter';
import Subscriptions from './pages/Subscriptions';
import Content from './pages/Content';
import ContentUpload from './pages/ContentUpload';
import ContentDetail from './pages/ContentDetail';
import ContentViewDetail from './pages/ContentViewDetail';
import Views from './pages/Views';
import Links from './pages/Links';
import Tags from './pages/Tags';
import OAuthCallback from './pages/OAuthCallback';
import EmailVerification from './pages/EmailVerification';
import Invitations from './pages/Invitations';
import AcceptInvitation from './pages/AcceptInvitation';
import PublicLinkAccess from './pages/PublicLinkAccess';
import PublicContentAccess from './pages/PublicContentAccess';

// New Marketing Pages
import HomePage from './pages/public/HomePage';
import FeaturesPage from './pages/public/FeaturesPage';
import PricingPage from './pages/public/PricingPage';
import AboutPage from './pages/public/AboutPage';
import HelpCenter from './pages/public/HelpCenter';
import GuideViewer from './pages/public/HelpCenter/GuideViewer';
import SystemStatusPublic from './pages/public/SystemStatus';
import LegalIndex from './pages/public/Legal';
import TermsOfService from './pages/public/Legal/TermsOfService';
import PrivacyPolicy from './pages/public/Legal/PrivacyPolicy';
import AcceptableUse from './pages/public/Legal/AcceptableUse';
import BillingTerms from './pages/public/Legal/BillingTerms';
import CookiePolicy from './pages/public/Legal/CookiePolicy';
import DPA from './pages/public/Legal/DPA';
import LandingPage from './pages/public/LandingPage';
import ComparePage from './pages/public/ComparePage';

import PublicHeader from './components/PublicHeader';
import PublicLayout from './components/PublicLayout';
import { CookieConsentBanner } from './components/CookieConsent';

// Define layout wrappers
const PublicPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="app">
    <PublicHeader />
    {children}
  </div>
);

const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SidebarLayout>{children}</SidebarLayout>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <ThemeSyncProvider>
              <NotificationProvider>
                <OrganizationProvider>
                  <RBACProvider>
                    <ConnectionStatusProvider> {/* Wrap with ConnectionStatusProvider */}
                      <ConnectionStatusAlert /> {/* Render the alert component */}
                      <SessionWarning /> {/* Render session warning component */}
                      <CookieConsentBanner /> {/* GDPR-compliant cookie consent - shows once */}
                    <Routes>
                {/* Public routes - Marketing Pages */}
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<HomePage />} />
                  <Route path="features" element={<FeaturesPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="about" element={<AboutPage />} />
                  <Route path="compare" element={<ComparePage />} />
                  <Route path="help" element={<HelpCenter />} />
                  <Route path="help/guide/:guideId" element={<GuideViewer />} />
                  <Route path="status" element={<SystemStatusPublic />} />
                  {/* Legal Routes */}
                  <Route path="legal">
                    <Route index element={<LegalIndex />} />
                    <Route path="terms" element={<TermsOfService />} />
                    <Route path="privacy" element={<PrivacyPolicy />} />
                    <Route path="acceptable-use" element={<AcceptableUse />} />
                    <Route path="billing" element={<BillingTerms />} />
                    <Route path="cookies" element={<CookiePolicy />} />
                    <Route path="dpa" element={<DPA />} />
                  </Route>
                </Route>

                {/* Public link access - No authentication required */}
                <Route path="/public/:linkId" element={<PublicLinkAccess />} />

                {/* Public content access via signed access token (click-through from shared views) */}
                <Route path="/p/content" element={<PublicContentAccess />} />

                {/* Landing pages — ad traffic, no nav/footer shell */}
                <Route path="/start" element={<LandingPage variant="general" />} />
                <Route path="/insurance" element={<LandingPage variant="insurance" />} />

                {/* Auth routes - No layout */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Register />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/oauth/callback" element={<OAuthCallback />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                
                {/* Protected routes */}
                <Route path="/" element={<ProtectedRoute />}>
                  <Route 
                    path="/dashboard" 
                    element={<ProtectedPage><Dashboard /></ProtectedPage>} 
                  />
                  <Route 
                    path="/dashboard-sample" 
                    element={<ProtectedPage><DashboardSample /></ProtectedPage>} 
                  />
                  <Route 
                    path="/content" 
                    element={<ProtectedPage><Content /></ProtectedPage>} 
                  />
                  <Route 
                    path="/content/upload" 
                    element={<ProtectedPage><ContentUpload /></ProtectedPage>} 
                  />
                  <Route 
                    path="/content/view/:viewId" 
                    element={<ProtectedPage><ContentViewDetail /></ProtectedPage>} 
                  />
                  <Route 
                    path="/content/:id" 
                    element={<ProtectedPage><ContentDetail /></ProtectedPage>} 
                  />
                  <Route
                    path="/views/*"
                    element={<ProtectedPage><Views /></ProtectedPage>}
                  />
                  <Route
                    path="/links"
                    element={<ProtectedPage><Links /></ProtectedPage>}
                  />
                  <Route
                    path="/tags"
                    element={<ProtectedPage><Tags /></ProtectedPage>}
                  />
                  {/* Organization routes */}
                  <Route 
                    path="/organizations/new" 
                    element={<ProtectedPage>
                      <OrganizationCreate />
                    </ProtectedPage>} 
                  />
                  <Route 
                    path="/organizations/:orgId" 
                    element={<ProtectedPage>
                      <OrganizationDetail />
                    </ProtectedPage>} 
                  />
                  <Route 
                    path="/organizations/:orgId/teams/new" 
                    element={<ProtectedPage>
                      <TeamCreate />
                    </ProtectedPage>} 
                  />
                  <Route 
                    path="/organizations/:orgId/teams/:teamId" 
                    element={<ProtectedPage>
                      <TeamDetail />
                    </ProtectedPage>} 
                  />
                  
                  {/* User routes */}
                  <Route 
                    path="/profile" 
                    element={<ProtectedPage>
                      <Profile />
                    </ProtectedPage>} 
                  />
                  <Route
                    path="/settings"
                    element={<ProtectedPage>
                      <Profile />
                    </ProtectedPage>}
                  />
                  <Route
                    path="/privacy-center"
                    element={<ProtectedPage>
                      <PrivacyCenter />
                    </ProtectedPage>}
                  />
                  <Route 
                    path="/subscriptions" 
                    element={<ProtectedPage>
                      <Subscriptions />
                    </ProtectedPage>} 
                  />
                  <Route
                    path="/invitations"
                    element={<ProtectedPage>
                      <Invitations />
                    </ProtectedPage>}
                  />
                  <Route
                    path="/invitations/accept"
                    element={<ProtectedPage>
                      <AcceptInvitation />
                    </ProtectedPage>}
                  />
                </Route>

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
                    </ConnectionStatusProvider>
                  </RBACProvider>
                </OrganizationProvider>
              </NotificationProvider>
            </ThemeSyncProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
